import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import {
    SpeakerSimpleHighIcon,
    SpeakerSimpleXIcon,
} from "@phosphor-icons/react/ssr";

import { cn } from "~/lib/utils";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { videoReviews } from "~/app/[code]/(main)/reviews/data/video-reviews";

type Props = {
    idx: number;
    activeIndex: number;
    name: string;
    mp4: string;
    hls: string;
    poster: string;
    isMuted: boolean;
    setMuted: Dispatch<SetStateAction<boolean>>;
    canPlay?: boolean;
    preload?: "none" | "metadata" | "auto";
    /** When false, the active slide is not auto-played (manual play still works). Default true. */
    autoPlayWhenActive?: boolean;
    /** When false, playback is paused and auto-play is suppressed. Default true. */
    hostVisible?: boolean;
    onUserInteraction?: (
        action: "play video" | "pause video" | "mute" | "unmute",
        video: HTMLVideoElement
    ) => void;
};

const NEIGHBOR_RANGE = 2;

export const VideoReview = ({
    idx,
    activeIndex,
    name,
    isMuted,
    setMuted,
    mp4,
    hls,
    poster,
    canPlay = true,
    preload = "auto",
    autoPlayWhenActive = true,
    hostVisible = true,
    onUserInteraction,
}: Props) => {
    const isActive = idx === activeIndex;

    const distance = Math.min(
        Math.abs(idx - activeIndex),
        videoReviews.length - Math.abs(idx - activeIndex)
    );
    const isInVisibleRange = canPlay && distance <= NEIGHBOR_RANGE;
    const [isVisible, setVisible] = useState(isInVisibleRange);

    if (isInVisibleRange && !isVisible) {
        setVisible(true);
    }

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const sendEvent = useTrackEvent();

    const sendVideoEvent = useCallback((event_name: string) => {
        if (!videoRef.current) return;

        const meta_data = {
            vsl_id: "review_vsl",
            video_name: mp4,
            vsl_duration: videoRef.current.duration,
            vsl_current_time: videoRef.current.currentTime,
            vsl_completed: videoRef.current.ended,
            vsl_sound: videoRef.current.muted ? "false" : "true",
            video_person_name: name,
        };

        sendEvent({
            event: "user_interaction",
            value: {
                widget: "real_testimonials",
                event_name,
                meta_data,
            },
        });
    }, [mp4, name, sendEvent]);

    const playVideo = useCallback(() => {
        if (!videoRef.current) return;

        if (videoRef.current.paused) {
            videoRef.current.play().then(() => {
                if (!onUserInteraction) {
                    sendVideoEvent("vsl_play");
                }
            }).catch(console.log);
        }
    }, [onUserInteraction, sendVideoEvent]);

    const pauseVideo = useCallback(() => {
        if (!videoRef.current) return;

        if (!videoRef.current.paused) {
            videoRef.current.pause();
            if (!onUserInteraction) {
                sendVideoEvent("vsl_stop");
            }
        }
    }, [onUserInteraction, sendVideoEvent]);

    const togglePlay = useCallback(() => {
        if (!videoRef.current || !canPlay) return;

        const video = videoRef.current;

        if (video.paused) {
            video.play().then(() => {
                if (onUserInteraction) {
                    onUserInteraction("play video", video);
                    return;
                }

                sendVideoEvent("vsl_play");
            }).catch(console.log);
        } else {
            video.pause();
            if (onUserInteraction) {
                onUserInteraction("pause video", video);
                return;
            }

            sendVideoEvent("vsl_stop");
        }
    }, [canPlay, onUserInteraction, sendVideoEvent]);

    const toggleSound = useCallback(() => {
        if (!videoRef.current) return;

        const action = isMuted ? "unmute" : "mute";
        setMuted(!isMuted);
        onUserInteraction?.(action, videoRef.current);
    }, [isMuted, onUserInteraction, setMuted]);

    useEffect(() => {
        if (!hostVisible) {
            pauseVideo();
            return;
        }

        if (!autoPlayWhenActive) {
            if (!isActive) {
                pauseVideo();
            }
            return;
        }

        if (isActive && canPlay) {
            playVideo();
        } else {
            pauseVideo();
        }
    }, [
        autoPlayWhenActive,
        canPlay,
        hostVisible,
        isActive,
        pauseVideo,
        playVideo,
    ]);

    return (
        <div className="relative cursor-pointer">
            <video
                ref={videoRef}
                className={cn("block w-full rounded-2xl object-cover bg-cover bg-center duration-500", isActive ? "aspect-[15/25]" : "aspect-[15/19]")}
                // Browsers hide the poster attribute once play() is called, before the first frame arrives
                style={{ backgroundImage: `url(${poster})` }}
                muted={isMuted}
                poster={poster}
                playsInline
                preload={preload}
                onClick={togglePlay}
            >
                {isVisible && hls && <source src={hls} type="application/x-mpegURL" />}
                {isVisible && <source src={mp4} type="video/mp4" />}
            </video>

            <button
                type="button"
                className="absolute bottom-4 right-4 w-10 h-10 flex items-center justify-center bg-trueBlack/25 backdrop-blur text-pureWhite rounded-full"
                aria-label={isMuted ? "Unmute video" : "Mute video"}
                onClick={toggleSound}
            >
                {isMuted ? <SpeakerSimpleXIcon size={24} weight="fill" /> : <SpeakerSimpleHighIcon size={24} weight="fill" />}
            </button>
        </div>
    );
};

export default VideoReview;
